/** Read-only metadata projection. Nothing here executes a connector or proves its grants. */
import { ValidationError } from "../utils/errors.js";
import { sanitizeErrorMessage } from "../utils/security.js";

const MAX_STATUSES = 20;
const STATUS_LABELS = new Set(["Connected", "Error", "Unauthenticated"]);
const SAFE_ERROR_CODES = new Set([
  "Unauthorized",
  "Forbidden",
  "InvalidConnection",
  "InvalidCredentials",
  "AuthenticationFailed",
  "ConnectionAuthorizationFailed",
]);
const SAFE_ERROR_MESSAGES = new Set([
  "token expired",
  "access denied",
  "unauthorized",
  "forbidden",
  "invalid credentials",
  "authentication failed",
  "connection not configured",
]);

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

// Do not echo unknown service text: even redacted free text can contain names or
// ticket content. The established sanitizer runs before the small generic allowlist.
function safeErrorPart(value: unknown, kind: "code" | "message"): string {
  if (value === undefined || value === null) return kind === "code" ? "error" : "no message";
  if (typeof value !== "string" || value.length > 256) return "[REDACTED]";
  const safe = sanitizeErrorMessage(value);
  const allowed =
    kind === "code" ? SAFE_ERROR_CODES.has(safe) : SAFE_ERROR_MESSAGES.has(safe.toLowerCase());
  return allowed ? safe : "[REDACTED]";
}

function timestamp(value: unknown): string | null {
  // ISO UTC timestamps only; no permissive Date.parse of arbitrary service text.
  if (
    typeof value !== "string" ||
    value.length > 28 ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?Z$/.test(value)
  )
    return null;
  const milliseconds = Date.parse(value);
  if (
    !Number.isFinite(milliseconds) ||
    new Date(milliseconds).toISOString().slice(0, 19) !== value.slice(0, 19)
  )
    return null;
  return value;
}

function matchingIdentity(value: unknown, expected: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string" || value !== expected) {
    // No returned identifier, URL, parameter or error body belongs in this error.
    throw new ValidationError(
      "Connection metadata returned a different or malformed target identity; no connector operation was performed.",
    );
  }
  return value;
}

export function projectConnectionMetadata(
  raw: unknown,
  requested: { connector: string; connectionName: string; environment: string },
) {
  const connection = record(raw);
  const properties = record(connection?.properties);
  const environment = record(properties?.environment);
  if (properties?.environment != null && !environment) {
    throw new ValidationError(
      "Connection metadata returned a malformed environment identity; no connector operation was performed.",
    );
  }
  const apiId = `/providers/Microsoft.PowerApps/apis/${requested.connector}`;
  const identity = {
    resourceId: matchingIdentity(
      connection?.id,
      `${apiId}/connections/${requested.connectionName}`,
    ),
    connectionName: matchingIdentity(connection?.name, requested.connectionName),
    connectorId: matchingIdentity(properties?.apiId, apiId),
    environmentName: matchingIdentity(environment?.name, requested.environment),
    environmentId: matchingIdentity(
      environment?.id,
      `/providers/Microsoft.PowerApps/environments/${requested.environment}`,
    ),
  };
  const identityVerification = Object.values(identity).every((value) => value !== null)
    ? "matched"
    : "unavailable";
  const rawStatuses = properties?.statuses;
  const statusArray = Array.isArray(rawStatuses) ? rawStatuses : null;
  const values: string[] = [];
  const descriptions: string[] = [];
  let incomplete = !statusArray || statusArray.length === 0 || statusArray.length > MAX_STATUSES;
  let hasError = false;
  for (const rawStatus of statusArray?.slice(0, MAX_STATUSES) ?? []) {
    const status = record(rawStatus);
    const label =
      typeof status?.status === "string" && STATUS_LABELS.has(status.status)
        ? status.status
        : "Unknown";
    if (label === "Unknown") incomplete = true;
    values.push(label);
    const error = record(status?.error);
    if (status?.error != null) {
      hasError = true;
      if (!error) incomplete = true;
    }
    descriptions.push(
      label +
        (status?.error != null
          ? ` (${safeErrorPart(error?.code, "code")}: ${safeErrorPart(error?.message, "message")})`
          : ""),
    );
  }
  const reportedConnected = values.includes("Connected");
  const mixed = new Set(values).size > 1 || (reportedConnected && hasError);
  const createdTime = timestamp(properties?.createdTime);
  const lastModifiedTime = timestamp(properties?.lastModifiedTime);
  // Count only: never read a test link's URI, method, token or other values.
  const testLinksCount = Array.isArray(properties?.testLinks) ? properties.testLinks.length : null;
  return {
    metadata: {
      source: "connection_metadata_get",
      identityVerification,
      identity,
      createdTime,
      lastModifiedTime,
      statuses: {
        values,
        count: statusArray?.length ?? null,
        reportedConnected,
        hasError,
        mixed,
        incomplete,
        truncated: (statusArray?.length ?? 0) > MAX_STATUSES,
      },
      testLinksCount,
      incomplete:
        identityVerification !== "matched" ||
        createdTime === null ||
        lastModifiedTime === null ||
        incomplete ||
        testLinksCount === null,
      verification: {
        renewedConsent: "not_verified",
        grantedScopes: "not_verified",
        runtimeAccess: "not_verified",
      },
    },
    statusDescription:
      descriptions.length === 0
        ? "no status reported"
        : descriptions.join(", ") +
          ((statusArray?.length ?? 0) > MAX_STATUSES ? ", additional statuses omitted" : ""),
  };
}
